import { get } from "lodash-es";
import {
  type Control,
  Controller,
  type FieldError,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import {
  BlDropdown,
  type BlDropdownProps,
  type BlDropdownValueType,
} from "Shared/Inputs/BlDropdown";
import type { BlOption } from "Shared/Inputs/BlOption";

export type BlFormDropdownProps<FormType extends FieldValues> = Omit<
  BlDropdownProps,
  "value" | "fieldError" | "onChange" | "name"
> & {
  options: BlOption[];
  name: FieldPath<FormType>;
  control: Control<FormType>;
  errors: FieldErrors<FormType>;
  /** Shorthand for labelProps.label */
  label?: string;
  /** Shorthand for labelProps.required */
  required?: boolean;
  onChange?: (value: BlDropdownValueType | null) => void;
};

export const BlFormDropdown = <T extends FieldValues>({
  name,
  control,
  errors,
  options,
  label,
  required,
  labelProps,
  onChange: externalOnChange,
  multiple,
  ...rest
}: BlFormDropdownProps<T>): React.ReactNode => {
  const mergedLabelProps =
    label !== undefined || required !== undefined
      ? { ...labelProps, label, required }
      : labelProps;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: controllerOnChange, value, onBlur } }) => {
        const isFilled =
          !!value &&
          (multiple
            ? options.some((opt) =>
                (value as Array<string | number>).some((v) => opt.value === v),
              )
            : options.some(
                (opt) =>
                  opt.value === value ||
                  opt.value.toString() === value?.toString(),
              ));

        const formValue = isFilled ? value : multiple ? [] : "";

        const handleChange = (val: BlDropdownValueType) => {
          controllerOnChange(val);
          externalOnChange?.(val);
        };

        return (
          <BlDropdown
            name={name}
            options={options}
            value={formValue}
            onChange={handleChange}
            onBlur={onBlur}
            multiple={multiple}
            labelProps={mergedLabelProps}
            fieldError={get(errors, name as string) as FieldError | undefined}
            {...rest}
          />
        );
      }}
    />
  );
};
